/**
 * R28-P5 voice-consent store. A single shared home for the user's voice-consent
 * state so the chat mic gate, the first-run disclosure dialog, and the Settings →
 * Voice toggle all read/mutate the same source of truth within a session.
 *
 * NOT persisted: the record is account-tied and server-owned (the backend stamps
 * the version + timestamp + audit trail). We always reload it from the server
 * rather than trusting a client cache — a stale cached "granted" would let the mic
 * bypass a server-side withdrawal.
 */
import { create } from 'zustand';

import { getVoiceConsent, grantVoiceConsent, withdrawVoiceConsent, type VoiceConsentRecord } from './voiceConsent';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

interface VoiceConsentState {
  record: VoiceConsentRecord | null;
  loadState: LoadState;
  mutating: boolean;
  /** Controls the first-run disclosure dialog (opened by the mic gate / settings). */
  promptOpen: boolean;
  error: string | null;
  actions: {
    load: () => Promise<void>;
    /** Grants consent. Resolves true on success. Closes the prompt on success. */
    grant: () => Promise<boolean>;
    /** Withdraws consent. Resolves true on success. */
    withdraw: () => Promise<boolean>;
    openPrompt: () => void;
    closePrompt: () => void;
  };
}

export const useVoiceConsentStore = create<VoiceConsentState>((set, get) => ({
  record: null,
  loadState: 'idle',
  mutating: false,
  promptOpen: false,
  error: null,
  actions: {
    load: async () => {
      set({ loadState: 'loading', error: null });
      try {
        const record = await getVoiceConsent();
        set({ record, loadState: 'ready' });
      } catch (err) {
        set({ loadState: 'error', error: err instanceof Error ? err.message : 'Failed to load voice consent' });
      }
    },
    grant: async () => {
      set({ mutating: true, error: null });
      try {
        const record = await grantVoiceConsent();
        set({ record, mutating: false, promptOpen: false });
        return true;
      } catch (err) {
        set({ mutating: false, error: err instanceof Error ? err.message : 'Failed to record consent' });
        return false;
      }
    },
    withdraw: async () => {
      set({ mutating: true, error: null });
      try {
        const record = await withdrawVoiceConsent();
        set({ record, mutating: false });
        return true;
      } catch (err) {
        set({ mutating: false, error: err instanceof Error ? err.message : 'Failed to withdraw consent' });
        return false;
      }
    },
    openPrompt: () => set({ promptOpen: true, error: null }),
    closePrompt: () => set({ promptOpen: false }),
  },
}));

/** True only when an active, un-withdrawn consent for the CURRENT disclosure version
 *  is on file. A version bump (currentVersion !== accepted version) re-requires consent. */
export function selectVoiceConsentGranted(s: VoiceConsentState): boolean {
  const r = s.record;
  if (!r || !r.granted) return false;
  // If the server bumped the disclosure version, the prior consent no longer covers
  // the current terms — treat as not-granted so the disclosure re-surfaces.
  if (r.version && r.currentVersion && r.version !== r.currentVersion) return false;
  return true;
}
